
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function LogosAdmin() {
  const [logos, setLogos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('logos-partenaires')
        .list('', { limit: 100 });

      if (error) throw error;

      const logoUrls = await Promise.all(
        data.map(async (file) => {
          const { data: urlData } = await supabase.storage
            .from('logos-partenaires')
            .createSignedUrl(file.name, 60 * 60 * 24 * 365); // 1 an
          return urlData?.signedUrl || '';
        })
      );

      setLogos(logoUrls.filter(url => url));
    } catch (error) {
      console.error('Erreur lors du chargement des logos:', error);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    setUploading(true);
    
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        
        const { error } = await supabase.storage
          .from('logos-partenaires')
          .upload(fileName, file);

        if (error) throw error;
        return fileName;
      });

      await Promise.all(uploadPromises);
      await loadLogos();
      alert(`${files.length} logo(s) uploadé(s) avec succès !`);
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert('Erreur lors de l\'upload des logos');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const deleteLogo = async (logoUrl: string) => {
    try {
      // Extraire le nom du fichier depuis l'URL
      const fileName = logoUrl.split('/').pop()?.split('?')[0];
      if (!fileName) return;

      const { error } = await supabase.storage
        .from('logos-partenaires')
        .remove([fileName]);

      if (error) throw error;
      
      await loadLogos();
      alert('Logo supprimé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du logo');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Gestion des Logos Partenaires
          </h1>

          {/* Zone d'upload */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Upload Multiple de Logos
            </h2>
            
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-300 hover:border-orange-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="space-y-4">
                <i className="ri-upload-cloud-line text-4xl text-gray-400"></i>
                <div>
                  <p className="text-lg text-gray-600 mb-2">
                    Glissez-déposez vos logos ici ou
                  </p>
                  <label className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer transition-colors">
                    <i className="ri-folder-open-line mr-2"></i>
                    Sélectionner des fichiers
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                      disabled={uploading}
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500">
                  Formats acceptés: PNG, JPG, SVG (max 5MB par fichier)
                </p>
              </div>
            </div>

            {uploading && (
              <div className="mt-4 text-center">
                <div className="inline-flex items-center text-orange-600">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Upload en cours...
                </div>
              </div>
            )}
          </div>

          {/* Liste des logos */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Logos Actuels ({logos.length})
            </h2>
            
            {logos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-image-line text-4xl mb-2"></i>
                <p>Aucun logo uploadé pour le moment</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {logos.map((logoUrl, index) => (
                  <div key={index} className="relative group">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <img
                        src={logoUrl}
                        alt={`Logo partenaire ${index + 1}`}
                        className="w-full h-16 object-contain"
                      />
                    </div>
                    <button
                      onClick={() => deleteLogo(logoUrl)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
                      title="Supprimer ce logo"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              <i className="ri-information-line mr-2"></i>
              Instructions
            </h3>
            <ul className="text-blue-700 space-y-1 text-sm">
              <li>• Vous pouvez sélectionner plusieurs fichiers en une seule fois</li>
              <li>• Les logos sont automatiquement redimensionnés pour l'affichage</li>
              <li>• Les URLs sont générées automatiquement et valides 1 an</li>
              <li>• Formats recommandés: PNG avec fond transparent pour un meilleur rendu</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
